import { Head, usePage } from '@inertiajs/react'
import { useState, type FormEvent } from 'react'
import { Mail } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { SharedProps } from '@/types'

const SUBJECT_OPTIONS = [
  'Account',
  'Advertising',
  'Billing',
  'Claim listing',
  'Event',
  'Event Services',
  'Feedback',
  'Hosting',
  'Issue',
  'Partnerships',
  'Press',
  'Other',
] as const

type FieldKey =
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'phone'
  | 'website'
  | 'organization'
  | 'subject'
  | 'message'

type FieldErrors = Partial<Record<FieldKey, string>>
type Status = 'idle' | 'loading' | 'success' | 'error'

export default function Contact() {
  const { app_url } = usePage<SharedProps>().props

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [website, setWebsite] = useState('')
  const [organization, setOrganization] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [nickname, setNickname] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [status, setStatus] = useState<Status>('idle')

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setStatus('loading')
    setErrors({})

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch('/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          website,
          organization,
          subject,
          message,
          nickname,
        }),
      })

      if (res.ok) {
        setStatus('success')
        return
      }

      if (res.status === 422) {
        const body = (await res.json()) as { errors?: FieldErrors }
        setErrors(body.errors ?? {})
      }
      setStatus('error')
    } catch {
      setStatus('error')
    }
  }

  return (
    <>
      <Head title="Contact Us">
        <meta
          head-key="description"
          name="description"
          content="Get in touch with the Gala Season team. We'd love to hear from organizations, attendees, and the press."
        />
        <meta head-key="og:title" property="og:title" content="Contact Us - Gala Season" />
        <meta head-key="og:type" property="og:type" content="website" />
        <meta head-key="og:url" property="og:url" content={`${app_url}/contact`} />
      </Head>

      <div className="relative overflow-hidden bg-linear-to-br from-[oklch(0.25_0.02_50)] via-[oklch(0.35_0.10_46)] to-primary">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(255,255,255,0.12),transparent)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <h1 className="max-w-[24ch] text-4xl font-semibold tracking-tight text-white text-balance sm:text-5xl lg:text-6xl">
            Get in touch
          </h1>
          <p className="mt-4 max-w-[60ch] text-lg font-medium text-white/85 text-pretty">
            Questions about hosting, partnerships, press, or feedback &mdash; tell us what's on your mind and we'll get back to you.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8">
        {status === 'success' ? (
          <div className="rounded-2xl bg-card p-8 text-center ring-1 ring-black/5">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <h2 className="mt-4 font-serif text-2xl font-semibold tracking-tight">
              Thanks &mdash; we got it.
            </h2>
            <p className="mt-2 text-base text-muted-foreground">
              We'll reply to <span className="font-medium text-foreground">{email}</span> as soon as we can.
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            noValidate
            className="rounded-2xl bg-card p-6 ring-1 ring-black/5 sm:p-8"
          >
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <Label htmlFor="contact_first_name" className="text-base font-semibold">
                  First name
                </Label>
                <Input
                  id="contact_first_name"
                  type="text"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  aria-invalid={errors.first_name ? true : undefined}
                  className="mt-2 h-11 text-base"
                />
                {errors.first_name && (
                  <p className="mt-1.5 text-sm text-red-600">{errors.first_name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="contact_last_name" className="text-base font-semibold">
                  Last name
                </Label>
                <Input
                  id="contact_last_name"
                  type="text"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  aria-invalid={errors.last_name ? true : undefined}
                  className="mt-2 h-11 text-base"
                />
                {errors.last_name && (
                  <p className="mt-1.5 text-sm text-red-600">{errors.last_name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="contact_email" className="text-base font-semibold">
                  Email
                </Label>
                <Input
                  id="contact_email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  aria-invalid={errors.email ? true : undefined}
                  className="mt-2 h-11 text-base"
                />
                {errors.email && <p className="mt-1.5 text-sm text-red-600">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="contact_phone" className="text-base font-semibold">
                  Phone <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="contact_phone"
                  type="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-2 h-11 text-base"
                />
                {errors.phone && <p className="mt-1.5 text-sm text-red-600">{errors.phone}</p>}
              </div>

              <div>
                <Label htmlFor="contact_organization" className="text-base font-semibold">
                  Organization / Company{' '}
                  <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="contact_organization"
                  type="text"
                  autoComplete="organization"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  className="mt-2 h-11 text-base"
                />
                {errors.organization && (
                  <p className="mt-1.5 text-sm text-red-600">{errors.organization}</p>
                )}
              </div>

              <div>
                <Label htmlFor="contact_website" className="text-base font-semibold">
                  Website <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="contact_website"
                  type="url"
                  autoComplete="url"
                  inputMode="url"
                  placeholder="https://"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  className="mt-2 h-11 text-base"
                />
                {errors.website && (
                  <p className="mt-1.5 text-sm text-red-600">{errors.website}</p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <Label htmlFor="contact_subject" className="text-base font-semibold">
                Subject
              </Label>
              <Select value={subject} onValueChange={setSubject}>
                <SelectTrigger
                  id="contact_subject"
                  size="default"
                  aria-invalid={errors.subject ? true : undefined}
                  className="mt-2 h-11 w-full text-base"
                >
                  <SelectValue placeholder="Select a topic" />
                </SelectTrigger>
                <SelectContent>
                  {SUBJECT_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.subject && (
                <p className="mt-1.5 text-sm text-red-600">{errors.subject}</p>
              )}
            </div>

            <div className="mt-6">
              <Label htmlFor="contact_message" className="text-base font-semibold">
                Message
              </Label>
              <Textarea
                id="contact_message"
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                aria-invalid={errors.message ? true : undefined}
                className="mt-2 text-base"
              />
              {errors.message && <p className="mt-1.5 text-sm text-red-600">{errors.message}</p>}
            </div>

            <input
              type="text"
              name="nickname"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className="absolute left-[-9999px] h-0 w-0 opacity-0"
            />

            <div className="mt-8 flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-end">
              {status === 'error' && (
                <p className="text-sm text-red-600 sm:mr-auto">
                  Something went wrong. Please check the fields and try again.
                </p>
              )}
              <Button type="submit" disabled={status === 'loading'} className="sm:w-auto">
                {status === 'loading' ? 'Sending...' : 'Send message'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </>
  )
}
